import React from 'react';
import { useNavigate } from 'react-router-dom';
import { VenueBooking } from '../../types/venue';
import { theme } from '../../theme';
import BookingStatusBadge from './BookingStatusBadge';
import {
  formatPrice,
  formatDate,
  formatTime,
  canCancelBooking,
} from '../../utils/venueHelpers';
import {
  MapPin,
  Eye,
  Users,
  XCircle,
} from 'lucide-react';

interface BookingCardProps {
  booking: VenueBooking;
  onCancel?: (bookingId: string) => void;
  onCreateMatch?: (booking: VenueBooking) => void;
}

const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  onCancel,
  onCreateMatch,
}) => {
  const navigate = useNavigate();
  const canCancel = canCancelBooking(booking);
  const canCreateMatch = booking.status === 'CONFIRMED' && !booking.matchId;

  return (
    <div style={{
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.card,
      padding: '14px 16px',
      marginBottom: '12px',
      border: `1px solid ${theme.colors.border}`,
    }}>
      {/* Row 1: Venue name + price + status */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '6px',
      }}>
        <span style={{
          fontSize: '1rem',
          fontWeight: theme.fonts.weightBold,
          color: theme.colors.text,
          fontFamily: theme.fonts.heading,
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {booking.venue?.name || 'Cancha'}
        </span>
        <span style={{
          fontSize: '1rem',
          fontWeight: theme.fonts.weightBold,
          color: theme.colors.primary,
          whiteSpace: 'nowrap',
        }}>
          {formatPrice(booking.totalAmount)}
        </span>
        <BookingStatusBadge status={booking.status} />
      </div>

      {/* Row 2: Date · Time · City */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: theme.fonts.sizeSmall,
        color: theme.colors.textSecondary,
        marginBottom: '12px',
        flexWrap: 'wrap',
      }}>
        <span>{formatDate(booking.bookingDate)}</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>{formatTime(booking.startTime)} - {formatTime(booking.endTime)}</span>
        {booking.venue?.city && (
          <>
            <span style={{ opacity: 0.5 }}>·</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
              <MapPin size={12} />
              {booking.venue.city}
            </span>
          </>
        )}
      </div>

      {/* Actions row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
      }}>
        {booking.matchId && (
          <button
            onClick={() => navigate(`/match/${booking.matchId}`)}
            style={{
              padding: '6px 12px', borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeSmall, fontWeight: theme.fonts.weightBold,
              cursor: 'pointer', border: 'none',
              backgroundColor: theme.colors.primary, color: '#000',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            <Users size={14} />
            Ver Partido
          </button>
        )}

        {canCreateMatch && onCreateMatch && (
          <button
            onClick={() => onCreateMatch(booking)}
            style={{
              padding: '6px 12px', borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeSmall, fontWeight: theme.fonts.weightBold,
              cursor: 'pointer', border: 'none',
              backgroundColor: theme.colors.primary, color: '#000',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            <Users size={14} />
            Crear partido
          </button>
        )}

        <button
          onClick={() => navigate(`/venues/${booking.venueId}`)}
          style={{
            padding: '6px 12px', borderRadius: theme.borderRadius.button,
            fontSize: theme.fonts.sizeSmall, fontWeight: theme.fonts.weightBold,
            cursor: 'pointer', backgroundColor: 'transparent',
            color: theme.colors.textSecondary,
            border: `1px solid ${theme.colors.border}`,
            display: 'flex', alignItems: 'center', gap: '4px',
          }}
        >
          <Eye size={14} />
          Ver cancha
        </button>

        {canCancel && onCancel && (
          <button
            onClick={() => onCancel(booking.id)}
            style={{
              padding: '6px 12px', borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeSmall, fontWeight: theme.fonts.weightBold,
              cursor: 'pointer', backgroundColor: 'transparent',
              color: theme.colors.danger,
              border: `1px solid ${theme.colors.danger}`,
              display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            <XCircle size={14} />
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
};

export default BookingCard;
